import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { Grid, Avatar, Menu, MenuItem } from '@mui/material';

import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import LogoutIcon from '@mui/icons-material/Logout';
import CalculateIcon from '@mui/icons-material/Calculate';
import AnalyticsIcon from '@mui/icons-material/Analytics';
import BarChartRoundedIcon from '@mui/icons-material/BarChartRounded';
import UpgradeRoundedIcon from '@mui/icons-material/UpgradeRounded';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import LocalAtmIcon from '@mui/icons-material/LocalAtm';
import ApiIcon from '@mui/icons-material/Api';

import { RoutesMap } from 'src/utils/const';
import { KPI_CARD_COLOR } from 'src/utils/colors';
import userIcon from 'src/App/assets/userIcon.png';
import { getTokenThunk } from 'src/store/Auth/actions';
import CurrencyChangeMenu from '../../../CurrencyChangeMenu';
import { clearApiCache } from 'src/utils/functions';
import { useCalculatorLandingPage, useOpenOverviewOnboardingPage } from 'src/utils/hooks';
import {
  getheadertext,
  getIfDemoMarketOptionVisible,
  handleDemoMarketOnClick,
  handleSubscriptionModalOpen,
} from '../../helper';
import { useSetUserPaymentInterest } from 'src/hooks/useCustomHooks';

const iconStyle = { marginRight: '3px' };

const UserMenu = ({
  logoutThunk,
  user,
  handleMarketSidebarClick,
  currency,
  setCurrency,
  setLoginModalOpen,
  centerCoord,
  address,
  isMobile,
  dummyMarket,
  setSubscriptionModalOpen,
  dashboardState,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [userNameInitials, setUserNameInitials] = useState('');
  const [showCredits, setShowCredits] = useState(false);
  const history = useHistory();
  const isLoggedIn = getTokenThunk()();
  const openOverviewOnboardingPage = useOpenOverviewOnboardingPage();
  const openCalculatorLandingPage = useCalculatorLandingPage();

  const { triggerPaymentInterest } = useSetUserPaymentInterest();

  const handleClick = ({ currentTarget }) => {
    setAnchorEl(currentTarget);
    localStorage.setItem('user-avatar-menu-opened', 'true');
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLoginLogout = async () => {
    if (isLoggedIn) {
      logoutThunk();
      await clearApiCache();
      history.push(RoutesMap.Home);
    } else {
      localStorage.setItem('estimatorCoords', JSON.stringify(centerCoord));
      localStorage.setItem('estimatorAddress', address);
      setLoginModalOpen(true);
    }
  };

  let userName = user && user.userName;
  if (!userName) {
    userName = 'Trial';
  }

  const getUserInitials = name => {
    const nameArray = name.split(' ');
    if (nameArray.length === 1) {
      return nameArray[0].slice(0, 2).toUpperCase();
    } else if (nameArray.length > 1) {
      const firstNameInitial = nameArray[0][0]?.toUpperCase();
      const lastNameInitial = nameArray[nameArray.length - 1][0]?.toUpperCase();
      return `${firstNameInitial}${lastNameInitial ? lastNameInitial : ''}`;
    } else {
      return '';
    }
  };

  useEffect(() => {
    if (user.userName != 'Trial') {
      let userNameInitials = getUserInitials(user.userName);
      setUserNameInitials(userNameInitials);
    }
    if (user.credits > 0 || user.estimatorCredits > 0 || user.subscriptionPlan !== 'starter') {
      setShowCredits(true);
    }
  }, [user]);

  const isDemoMarketOptionVisible = getIfDemoMarketOptionVisible(
    window.location.pathname,
    dummyMarket
  );

  const openSettings = () => {
    handleMarketSidebarClick('settings');
  };

  const handleSearchListingBtnClick = () => {
    handleMarketSidebarClick('searchListing');
    history.push('/search-listing');
  };

  const handleAffiliateDashboardClick = () => {
    window.open('/affiliate', '_blank');
  };

  const handleApiDashboardClick = () => {
    window.open('/api-dashboard', '_blank');
  };

  return (
    <Grid item>
      <Avatar
        sx={{
          width: 46,
          height: 46,
          cursor: 'pointer',
          backgroundColor: KPI_CARD_COLOR,
          color: '#ffff',
          fontSize: '25px',
          boxShadow: '0 2px 10px -1px #0002',
        }}
        onClick={handleClick}
        src={
          user.googleProfileImage ? user.googleProfileImage : userNameInitials == '' ? userIcon : ''
        }
      >
        {userNameInitials}
      </Avatar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: '10px',
          },
        }}
      >
        {isMobile && user.subscriptionPlan !== 'scale' && (
          <MenuItem
            onClick={() => {
              triggerPaymentInterest();
              handleSubscriptionModalOpen(
                isLoggedIn,
                centerCoord,
                address,
                setLoginModalOpen,
                setSubscriptionModalOpen
              );
              handleClose();
            }}
          >
            <UpgradeRoundedIcon sx={iconStyle} />
            {getheadertext(dashboardState, user, showCredits)}
          </MenuItem>
        )}
        {isMobile && isDemoMarketOptionVisible && user.subscriptionPlan === 'starter' && (
          <MenuItem
            onClick={() => {
              handleDemoMarketOnClick(dummyMarket);
              handleClose();
            }}
          >
            <BarChartRoundedIcon sx={iconStyle} />
            Demo Market
          </MenuItem>
        )}
        <MenuItem
          onClick={() => {
            openSettings();
            handleClose();
          }}
        >
          <AccountCircleIcon sx={iconStyle} />
          Your Account
        </MenuItem>
        <MenuItem
          onClick={() => {
            openOverviewOnboardingPage();
            handleClose();
          }}
        >
          <AnalyticsIcon sx={iconStyle} />
          My Markets
        </MenuItem>
        <MenuItem
          onClick={() => {
            openCalculatorLandingPage();
            handleClose();
          }}
        >
          <CalculateIcon sx={iconStyle} />
          My Reports
        </MenuItem>
        <MenuItem
          onClick={() => {
            handleSearchListingBtnClick();
            handleClose();
          }}
        >
          <ManageSearchIcon sx={iconStyle} />
          Search Listing
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleApiDashboardClick();
            handleClose();
          }}
        >
          <ApiIcon sx={iconStyle} />
          API Dashboard
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleAffiliateDashboardClick();
            handleClose();
          }}
        >
          <LocalAtmIcon sx={iconStyle} />
          Affiliate Dashboard
        </MenuItem>

        <MenuItem
          onClick={() => {
            handleLoginLogout();
            handleClose();
          }}
        >
          <LogoutIcon sx={iconStyle} />
          {isLoggedIn ? 'Log out' : 'Log In'}
        </MenuItem>
        {isMobile && (
          <MenuItem style={{ padding: '0px' }}>
            <CurrencyChangeMenu currency={currency} setCurrency={setCurrency} fullWidth={true} />
          </MenuItem>
        )}
      </Menu>
    </Grid>
  );
};

export default UserMenu;
